import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  RefreshCw, HardDrive, Database, Shield, AlertTriangle,
  Hash, CheckCircle, Clock, XCircle, BarChart3, Zap,
  Film, Eye, X, FileQuestion, AlertOctagon, Play, Loader,
  Search,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ENV_COLORS = {
  prod: '#f59e0b',
  dev: '#14b8a6',
  system: '#3b82f6',
  quarantine: '#6b7280',
  unknown: '#ef4444',
};

const TYPE_COLORS = {
  original_videos: '#8b5cf6',
  proxy_videos: '#a78bfa',
  image_uploads: '#06b6d4',
  image_exports: '#22d3ee',
  video_exports: '#ec4899',
  backgrounds: '#10b981',
  presets: '#34d399',
  backups: '#6366f1',
  system_files: '#94a3b8',
  orphan_candidates: '#f87171',
  unknown: '#dc2626',
};

const OWNER_COLORS = {
  registered: '#f59e0b',
  anonymous: '#14b8a6',
  system: '#3b82f6',
  unknown: '#ef4444',
};

// ── Preview helpers (reuses admin_storage preview endpoint) ──────────────
const IMAGE_EXTS = /\.(png|jpe?g|webp|gif)$/i;
const VIDEO_EXTS = /\.(mp4|mov|avi|mkv|webm)$/i;

export default function AdminAnalytics() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('admin_password') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [hashStatus, setHashStatus] = useState(null);
  const [dedupStatus, setDedupStatus] = useState(null);
  const [renderCacheStatus, setRenderCacheStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [videoPreviewKey, setVideoPreviewKey] = useState(null);
  const [usageModal, setUsageModal] = useState(null); // { asset_id, storage_key }

  // Schema migration gate state
  const [schemaStatus, setSchemaStatus] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaNeedsMigration, setSchemaNeedsMigration] = useState(false);
  const [migrateConfirm, setMigrateConfirm] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [migrateError, setMigrateError] = useState(null);
  const [analyticsSchemaError, setAnalyticsSchemaError] = useState(false);

  const headers = useCallback(() => ({
    'X-Admin-Password': password,
    'Content-Type': 'application/json',
  }), [password]);

  // ── Schema status check ────────────────────────────────────────────────
  const fetchSchemaStatus = useCallback(async () => {
    setSchemaLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/schema/status`, {
        headers: headers(),
      });
      if (resp.status === 401) {
        setAuthenticated(false);
        return false;
      }
      if (!resp.ok) return false;
      const data = await resp.json();
      setSchemaStatus(data);
      // Only block the page if v010 ANALYTICS PREREQUISITES are missing.
      // Pending migrations like v011 (render cache tracking) should NOT
      // block Analytics/Hash/Dedup — only the Render Cache panel shows disabled.
      const needsMigration = !data.prerequisites.satisfied;
      setSchemaNeedsMigration(needsMigration);
      return !needsMigration; // true = schema OK, proceed to analytics
    } catch (e) {
      // If schema status endpoint fails, don't block — let analytics try
      return true;
    } finally {
      setSchemaLoading(false);
    }
  }, [headers]);

  // ── Apply migration ────────────────────────────────────────────────────
  const handleApplyMigration = async () => {
    setMigrating(true);
    setMigrateError(null);
    setMigrateResult(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/schema/apply`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ confirm: 'MIGRATE' }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setMigrateError(data.detail || `HTTP ${resp.status}`);
        return;
      }
      setMigrateResult(data);
      // Re-check schema status
      const schemaOk = await fetchSchemaStatus();
      if (schemaOk) {
        // Schema is now OK — fetch analytics
        setSchemaNeedsMigration(false);
        setAnalyticsSchemaError(false);
        await fetchAnalytics(true);
        await fetchHashStatus();
        await fetchDedupStatus();
        await fetchRenderCacheStatus();
      }
    } catch (e) {
      setMigrateError(e.message);
    } finally {
      setMigrating(false);
    }
  };

  const fetchAnalytics = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    setAnalyticsSchemaError(false);
    try {
      const url = `${BACKEND_URL}/api/admin/analytics/storage-distribution${refresh ? '?refresh=true' : ''}`;
      const resp = await fetch(url, { headers: headers() });
      if (resp.status === 401) {
        setAuthenticated(false);
        throw new Error('Invalid password');
      }
      if (resp.status === 503) {
        const data = await resp.json();
        if (data.error === 'schema_missing') {
          setAnalyticsSchemaError(true);
          setSchemaNeedsMigration(true);
          setAuthenticated(true);
          // Fetch schema status for migration gate
          await fetchSchemaStatus();
          return;
        }
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setAnalytics(data);
      setAuthenticated(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers, fetchSchemaStatus]);

  const fetchHashStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/operations/hash-status`, {
        headers: headers(),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.error !== 'schema_missing') {
          setHashStatus(data);
        }
      }
    } catch (e) {
      // Non-critical
    }
  }, [headers]);

  const fetchDedupStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/operations/dedup-status`, {
        headers: headers(),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.error !== 'schema_missing') {
          setDedupStatus(data);
        }
      }
    } catch (e) {
      // Non-critical
    }
  }, [headers]);

  const fetchRenderCacheStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/operations/render-cache/summary`, {
        headers: headers(),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.error !== 'schema_missing') {
          setRenderCacheStatus(data);
        }
      }
    } catch (e) {
      // Non-critical
    }
  }, [headers]);

  const handleLogin = async (e) => {
    e.preventDefault();
    sessionStorage.setItem('admin_password', password);
    // Check schema status first
    const schemaOk = await fetchSchemaStatus();
    if (schemaOk) {
      await fetchAnalytics();
      await fetchHashStatus();
      await fetchDedupStatus();
      await fetchRenderCacheStatus();
    } else {
      // Schema needs migration — still mark as authenticated to show the gate
      setAuthenticated(true);
      // Try to load analytics anyway (will get 503 gracefully)
      await fetchAnalytics();
    }
  };

  const handleRefresh = async () => {
    await fetchAnalytics(true);
    await fetchHashStatus();
    await fetchDedupStatus();
    await fetchRenderCacheStatus();
  };

  useEffect(() => {
    if (password) {
      (async () => {
        const schemaOk = await fetchSchemaStatus();
        if (schemaOk) {
          await fetchAnalytics();
          await fetchHashStatus();
          await fetchDedupStatus();
          await fetchRenderCacheStatus();
        } else {
          setAuthenticated(true);
          await fetchAnalytics();
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_password');
    setPassword('');
    setAuthenticated(false);
    setAnalytics(null);
    setSchemaStatus(null);
    setSchemaNeedsMigration(false);
    setMigrateResult(null);
    setMigrateError(null);
    setAnalyticsSchemaError(false);
  };

  if (!authenticated) {
    return (
      <div style={styles.loginContainer}>
        <div style={styles.loginCard}>
          <BarChart3 size={32} color="#f59e0b" />
          <h2 style={styles.loginTitle}>Storage Analytics</h2>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Admin password"
              style={styles.loginInput}
              autoFocus
            />
            <button type="submit" style={styles.loginButton} disabled={schemaLoading}>
              {schemaLoading ? 'Checking…' : 'Access Analytics'}
            </button>
          </form>
          {error && <div style={styles.errorBanner}>{error}</div>}
        </div>
      </div>
    );
  }

  // ── Migration Gate ──────────────────────────────────────────────────────
  const showMigrationGate = schemaNeedsMigration || analyticsSchemaError;

  return (
    <AdminLayout activePage="analytics" onLogout={handleLogout}>
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Storage Analytics</h1>
            {analytics && (
              <span style={styles.subtitle}>
                Last computed: {_timeAgo(analytics.computed_at)}
                {analytics.from_cache && ' (cached)'}
                {analytics.computation_ms && ` · ${analytics.computation_ms}ms`}
              </span>
            )}
          </div>
          {!showMigrationGate && (
            <button
              onClick={handleRefresh}
              disabled={loading}
              style={styles.refreshBtn}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Computing…' : 'Refresh'}
            </button>
          )}
        </div>

        {error && !showMigrationGate && <div style={styles.errorBanner}>{error}</div>}

        {/* Migration Gate Card */}
        {showMigrationGate && (
          <MigrationGate
            schemaStatus={schemaStatus}
            migrateConfirm={migrateConfirm}
            setMigrateConfirm={setMigrateConfirm}
            migrating={migrating}
            migrateResult={migrateResult}
            migrateError={migrateError}
            onApply={handleApplyMigration}
            schemaLoading={schemaLoading}
          />
        )}

        {analytics && (
          <>
            {/* Summary Cards */}
            <div style={styles.cardGrid}>
              <SummaryCard
                icon={<HardDrive size={18} />}
                label="Total S3 Usage"
                value={analytics.totals.size_human}
                sub={`${analytics.totals.objects} objects`}
                color="#f59e0b"
              />
              {['prod', 'dev', 'system', 'quarantine', 'unknown'].map(env => {
                const data = analytics.by_environment[env];
                if (!data) return null;
                return (
                  <SummaryCard
                    key={env}
                    icon={env === 'quarantine' ? <Shield size={18} /> :
                          env === 'unknown' ? <AlertTriangle size={18} /> :
                          <Database size={18} />}
                    label={env.charAt(0).toUpperCase() + env.slice(1)}
                    value={data.size_human}
                    sub={`${data.objects} objects`}
                    color={ENV_COLORS[env]}
                  />
                );
              })}
              <SummaryCard
                icon={<AlertTriangle size={18} />}
                label="Estimated Reclaimable"
                value={analytics.reclaimable.total.size_human || '0 B'}
                sub={`${analytics.reclaimable.total.objects} items`}
                color="#ef4444"
              />
            </div>

            {/* Charts Row */}
            <div style={styles.chartsRow}>
              {/* Donut: By Environment */}
              <div style={styles.chartCard}>
                <h3 style={styles.chartTitle}>Usage by Environment</h3>
                <DonutChart
                  data={Object.entries(analytics.by_environment).map(([name, d]) => ({
                    name, value: d.size_bytes, label: d.size_human,
                  }))}
                  colors={ENV_COLORS}
                />
              </div>

              {/* Donut: By Object Type */}
              <div style={styles.chartCard}>
                <h3 style={styles.chartTitle}>Usage by Object Type</h3>
                <DonutChart
                  data={Object.entries(analytics.by_object_type)
                    .filter(([, d]) => d.size_bytes > 0)
                    .map(([name, d]) => ({
                      name: _typeLabel(name), value: d.size_bytes, label: d.size_human,
                    }))}
                  colors={TYPE_COLORS}
                  useIndex
                />
              </div>

              {/* Donut: By Owner Type */}
              <div style={styles.chartCard}>
                <h3 style={styles.chartTitle}>Usage by Owner Type</h3>
                <DonutChart
                  data={Object.entries(analytics.by_owner_type).map(([name, d]) => ({
                    name: name.charAt(0).toUpperCase() + name.slice(1),
                    value: d.size_bytes,
                    label: d.size_human,
                  }))}
                  colors={OWNER_COLORS}
                  useIndex
                />
              </div>
            </div>

            {/* Stacked Bar: Env × Type */}
            {analytics.by_env_and_type && (
              <div style={styles.fullWidthCard}>
                <h3 style={styles.chartTitle}>Object Type Breakdown by Environment</h3>
                <StackedBarChart data={analytics.by_env_and_type} />
              </div>
            )}

            {/* Insight Cards */}
            <div style={styles.insightGrid}>
              <InsightCard
                title="Expired Renders"
                count={analytics.reclaimable.expired_renders.objects}
                size={_formatSize(analytics.reclaimable.expired_renders.size_bytes)}
                subtitle="Past TTL"
              />
              <InsightCard
                title="Stale Uploads"
                count={analytics.reclaimable.stale_uploads.objects}
                size={_formatSize(analytics.reclaimable.stale_uploads.size_bytes)}
                subtitle="Unconfirmed"
              />
              <InsightCard
                title="Quarantined"
                count={analytics.reclaimable.quarantined.objects}
                size={_formatSize(analytics.reclaimable.quarantined.size_bytes)}
                subtitle="Awaiting review"
              />
              <InsightCard
                title="Dev Orphans"
                count={analytics.reclaimable.dev_orphans.objects}
                size={_formatSize(analytics.reclaimable.dev_orphans.size_bytes)}
                subtitle="Sandbox/test data"
              />
            </div>

            {/* Hash Status */}
            {hashStatus && (
              <div style={styles.fullWidthCard}>
                <h3 style={styles.chartTitle}>
                  <Hash size={16} style={{ marginRight: 8 }} />
                  Hash Infrastructure Status
                </h3>
                <div style={styles.hashGrid}>
                  <HashCard
                    icon={<Database size={16} />}
                    label="Total Assets"
                    value={hashStatus.assets.total}
                    color="#94a3b8"
                  />
                  <HashCard
                    icon={<CheckCircle size={16} />}
                    label="Hashed"
                    value={hashStatus.assets.hashed}
                    color="#10b981"
                  />
                  <HashCard
                    icon={<Clock size={16} />}
                    label="Pending"
                    value={hashStatus.assets.unhashed_confirmed}
                    color="#f59e0b"
                  />
                  <HashCard
                    icon={<BarChart3 size={16} />}
                    label="Coverage"
                    value={`${hashStatus.assets.hash_percentage}%`}
                    color="#3b82f6"
                  />
                  <div style={styles.hashDivider} />
                  <HashCard
                    icon={<Clock size={16} />}
                    label="Jobs Queued"
                    value={hashStatus.jobs.queued}
                    color="#f59e0b"
                  />
                  <HashCard
                    icon={<RefreshCw size={16} />}
                    label="Jobs Running"
                    value={hashStatus.jobs.running}
                    color="#3b82f6"
                  />
                  <HashCard
                    icon={<CheckCircle size={16} />}
                    label="Jobs Completed"
                    value={hashStatus.jobs.completed}
                    color="#10b981"
                  />
                  <HashCard
                    icon={<XCircle size={16} />}
                    label="Jobs Failed"
                    value={hashStatus.jobs.failed}
                    color="#ef4444"
                  />
                  <div style={styles.hashDivider} />
                  <HashCard
                    icon={<Zap size={16} />}
                    label="Hash Worker Execution"
                    value={hashStatus.hash_verify_enabled ? 'Enabled' : 'Disabled / Queued Only'}
                    color={hashStatus.hash_verify_enabled ? '#10b981' : '#737373'}
                  />
                </div>
              </div>
            )}

            {/* Upload Dedup Status */}
            {dedupStatus && (
              <div style={styles.fullWidthCard}>
                <h3 style={styles.chartTitle}>
                  <Shield size={16} color="#3b82f6" style={{ marginRight: 8 }} />
                  Upload Dedup Status
                </h3>
                <div style={styles.hashGrid}>
                  <HashCard
                    icon={<CheckCircle size={16} />}
                    label="Dedup Hits"
                    value={dedupStatus.hits}
                    color="#10b981"
                  />
                  <HashCard
                    icon={<Database size={16} />}
                    label="Duplicate Groups"
                    value={dedupStatus.duplicate_groups}
                    color="#f59e0b"
                  />
                  <HashCard
                    icon={<HardDrive size={16} />}
                    label="Excess Copies"
                    value={dedupStatus.excess_copies}
                    color="#f59e0b"
                  />
                  <HashCard
                    icon={<BarChart3 size={16} />}
                    label="Potential Savings"
                    value={dedupStatus.potential_savings_human || '0 B'}
                    color="#8b5cf6"
                  />
                  <div style={styles.hashDivider} />
                  <HashCard
                    icon={<Zap size={16} />}
                    label="Dedup Execution"
                    value={dedupStatus.enabled ? 'Enabled' : 'Disabled'}
                    color={dedupStatus.enabled ? '#10b981' : '#737373'}
                  />
                </div>
              </div>
            )}

            {/* Render Cache Status */}
            {renderCacheStatus && (
              <div style={styles.fullWidthCard}>
                <h3 style={styles.chartTitle}>
                  <Zap size={16} color="#8b5cf6" style={{ marginRight: 8 }} />
                  Render Cache Status
                </h3>
                <div style={styles.hashGrid}>
                  <HashCard
                    icon={<BarChart3 size={16} />}
                    label="Total Renders"
                    value={renderCacheStatus.total_renders}
                    color="#a3a3a3"
                  />
                  <HashCard
                    icon={<CheckCircle size={16} />}
                    label="Cache Hits"
                    value={renderCacheStatus.cache_hits}
                    color="#10b981"
                  />
                  <HashCard
                    icon={<Zap size={16} />}
                    label="Hit Rate"
                    value={renderCacheStatus.total_renders > 0
                      ? `${(renderCacheStatus.cache_hit_rate * 100).toFixed(1)}%`
                      : '—'}
                    color="#8b5cf6"
                  />
                  <HashCard
                    icon={<Database size={16} />}
                    label="Active Cached"
                    value={renderCacheStatus.active_cached_outputs}
                    color="#3b82f6"
                  />
                  <HashCard
                    icon={<Clock size={16} />}
                    label="Expired Pending"
                    value={renderCacheStatus.expired_pending_cleanup}
                    color="#f59e0b"
                  />
                  <HashCard
                    icon={<AlertTriangle size={16} />}
                    label="Failures (24h)"
                    value={renderCacheStatus.recent_failures_24h}
                    color={renderCacheStatus.recent_failures_24h > 0 ? '#ef4444' : '#525252'}
                  />
                  <div style={styles.hashDivider} />
                  <HashCard
                    icon={<Zap size={16} />}
                    label="Render Cache"
                    value={renderCacheStatus.render_cache_enabled ? 'Enabled' : 'Disabled'}
                    color={renderCacheStatus.render_cache_enabled ? '#10b981' : '#737373'}
                  />
                  <HashCard
                    icon={<Hash size={16} />}
                    label="Profile Version"
                    value={`v${renderCacheStatus.render_profile_version}`}
                    color="#a3a3a3"
                  />
                  {!renderCacheStatus.has_source_job_id_column && (
                    <HashCard
                      icon={<AlertTriangle size={16} />}
                      label="v011 Migration"
                      value="Required"
                      color="#f59e0b"
                    />
                  )}
                </div>
              </div>
            )}
            {!renderCacheStatus && !showMigrationGate && (
              <div style={{ ...styles.fullWidthCard, opacity: 0.6 }}>
                <h3 style={styles.chartTitle}>
                  <Zap size={16} color="#525252" style={{ marginRight: 8 }} />
                  Render Cache Status
                </h3>
                <p style={{ color: '#525252', fontSize: 12, margin: 0 }}>
                  Requires v010+v011 schema migrations. Apply pending migrations to enable.
                </p>
              </div>
            )}

            {/* Top Objects Table */}
            {analytics.top_objects && analytics.top_objects.length > 0 && (
              <div style={styles.fullWidthCard}>
                <h3 style={styles.chartTitle}>Top Largest Objects</h3>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.th, width: 52 }}>Preview</th>
                        <th style={styles.th}>Key</th>
                        <th style={styles.th}>Size</th>
                        <th style={styles.th}>Environment</th>
                        <th style={styles.th}>Type</th>
                        <th style={styles.th}>Tracked</th>
                        <th style={{ ...styles.th, width: 60 }}>Inspect</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.top_objects.slice(0, 15).map((obj, i) => {
                        const isVideo = VIDEO_EXTS.test(obj.key || '');
                        const isImage = IMAGE_EXTS.test(obj.key || '');
                        return (
                          <tr key={i} style={i % 2 === 0 ? styles.trEven : {}}>
                            <td style={{ ...styles.td, padding: '4px 8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <ObjectPreview s3Key={obj.key} headers={headers()} />
                                {isVideo && (
                                  <button
                                    onClick={() => setVideoPreviewKey(obj.key)}
                                    title="Preview video"
                                    style={styles.previewBtn}
                                  >
                                    <Eye size={12} />
                                  </button>
                                )}
                                {isImage && (
                                  <button
                                    onClick={() => {
                                      // Open image in new tab via presigned URL
                                      fetch(`${BACKEND_URL}/api/admin/storage/preview?key=${encodeURIComponent(obj.key)}`, { headers: headers() })
                                        .then(r => r.ok ? r.json() : Promise.reject())
                                        .then(data => window.open(data.url, '_blank'))
                                        .catch(() => {});
                                    }}
                                    title="Open full size"
                                    style={styles.previewBtn}
                                  >
                                    <Eye size={12} />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td style={styles.tdKey} title={obj.key}>
                              {_truncateKey(obj.key)}
                            </td>
                            <td style={styles.td}>{obj.size_human}</td>
                            <td style={styles.td}>
                              <span style={{
                                ...styles.envBadge,
                                backgroundColor: (ENV_COLORS[obj.environment] || '#666') + '22',
                                color: ENV_COLORS[obj.environment] || '#666',
                              }}>
                                {obj.environment}
                              </span>
                            </td>
                            <td style={styles.td}>{_typeLabel(obj.object_type)}</td>
                            <td style={styles.td}>
                              {obj.tracked ?
                                <CheckCircle size={14} color="#10b981" /> :
                                <AlertTriangle size={14} color="#f59e0b" />
                              }
                            </td>
                            <td style={styles.td}>
                              <button
                                onClick={() => setUsageModal({ storage_key: obj.key })}
                                title="Inspect usage — why tracked?"
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 3,
                                  padding: '2px 8px', background: '#1a1a2e', borderRadius: 4,
                                  border: '1px solid #2d2d5e', color: '#818cf8', fontSize: 10,
                                  cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
                                }}
                              >
                                <Search size={10} /> Usage
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Groups Table */}
            {analytics.top_groups && analytics.top_groups.length > 0 && (
              <div style={styles.fullWidthCard}>
                <h3 style={styles.chartTitle}>Top Groups by Prefix</h3>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Prefix</th>
                        <th style={styles.th}>Objects</th>
                        <th style={styles.th}>Total Size</th>
                        <th style={styles.th}>Environment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.top_groups.slice(0, 10).map((g, i) => (
                        <tr key={i} style={i % 2 === 0 ? styles.trEven : {}}>
                          <td style={styles.tdKey}>{g.prefix}</td>
                          <td style={styles.td}>{g.count}</td>
                          <td style={styles.td}>{g.total_size_human}</td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.envBadge,
                              backgroundColor: (ENV_COLORS[g.environment] || '#666') + '22',
                              color: ENV_COLORS[g.environment] || '#666',
                            }}>
                              {g.environment}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* Video Preview Modal */}
      {videoPreviewKey && (
        <VideoPreviewModal
          s3Key={videoPreviewKey}
          headers={headers()}
          onClose={() => setVideoPreviewKey(null)}
        />
      )}
      {usageModal && (
        <AnalyticsUsageModal
          storageKey={usageModal.storage_key}
          headers={headers()}
          onClose={() => setUsageModal(null)}
        />
      )}
    </AdminLayout>
  );
}


// ── Migration Gate Component ─────────────────────────────────────────────

function MigrationGate({
  schemaStatus, migrateConfirm, setMigrateConfirm,
  migrating, migrateResult, migrateError, onApply, schemaLoading,
}) {
  const pending = schemaStatus?.pending_migrations || [];
  const prereqs = schemaStatus?.prerequisites || {};
  const missingCols = prereqs.missing_columns || [];
  const missingTables = prereqs.missing_tables || [];
  const confirmValid = migrateConfirm === 'MIGRATE';

  return (
    <div style={gateStyles.container}>
      <div style={gateStyles.card}>
        <div style={gateStyles.iconRow}>
          <AlertOctagon size={28} color="#f59e0b" />
        </div>
        <h2 style={gateStyles.title}>Database Schema Update Required</h2>
        <p style={gateStyles.description}>
          The current code requires schema changes that have not been applied to the database yet.
          Storage Analytics, Hash Infrastructure, and Dedup Status cannot load until these
          migrations are applied.
        </p>

        {/* Pending Migrations */}
        {pending.length > 0 && (
          <div style={gateStyles.section}>
            <h3 style={gateStyles.sectionTitle}>Pending Migrations</h3>
            {pending.map(m => (
              <div key={m.version} style={gateStyles.migrationItem}>
                <Database size={14} color="#f59e0b" style={{ flexShrink: 0 }} />
                <span style={gateStyles.migrationVersion}>v{m.version}</span>
                <span style={gateStyles.migrationName}>{m.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Missing Prerequisites */}
        {(missingCols.length > 0 || missingTables.length > 0) && (
          <div style={gateStyles.section}>
            <h3 style={gateStyles.sectionTitle}>Missing Schema Objects</h3>
            {missingCols.map((c, i) => (
              <div key={i} style={gateStyles.missingItem}>
                <XCircle size={12} color="#ef4444" style={{ flexShrink: 0 }} />
                <span>Column <code style={gateStyles.code}>{c.table}.{c.column}</code></span>
              </div>
            ))}
            {missingTables.map((t, i) => (
              <div key={i} style={gateStyles.missingItem}>
                <XCircle size={12} color="#ef4444" style={{ flexShrink: 0 }} />
                <span>Table <code style={gateStyles.code}>{t}</code></span>
              </div>
            ))}
          </div>
        )}

        {/* What this provides */}
        <div style={gateStyles.section}>
          <h3 style={gateStyles.sectionTitle}>What This Enables</h3>
          <div style={gateStyles.featureList}>
            <div style={gateStyles.featureItem}>
              <CheckCircle size={12} color="#525252" />
              <span>Storage Analytics — usage breakdown by environment, type, and owner</span>
            </div>
            <div style={gateStyles.featureItem}>
              <CheckCircle size={12} color="#525252" />
              <span>Hash Infrastructure — SHA-256 content hashing for dedup</span>
            </div>
            <div style={gateStyles.featureItem}>
              <CheckCircle size={12} color="#525252" />
              <span>Upload Dedup — duplicate detection and savings estimation</span>
            </div>
            <div style={gateStyles.featureItem}>
              <CheckCircle size={12} color="#525252" />
              <span>Audit Log — append-only history of all storage operations</span>
            </div>
          </div>
        </div>

        {/* Safety notice */}
        <div style={gateStyles.safetyNotice}>
          <Shield size={14} color="#3b82f6" style={{ flexShrink: 0 }} />
          <span>
            This migration is <strong>additive only</strong> — it adds new columns and tables
            using <code style={gateStyles.code}>IF NOT EXISTS</code>. No existing data is
            modified, deleted, or moved. No S3 objects are touched.
          </span>
        </div>

        {/* Migration Result */}
        {migrateResult && (
          <div style={{
            ...gateStyles.resultBox,
            borderColor: migrateResult.error_count > 0 ? '#ef444466' : '#10b98166',
            background: migrateResult.error_count > 0 ? '#ef444411' : '#10b98111',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              {migrateResult.error_count > 0 ?
                <AlertTriangle size={16} color="#ef4444" /> :
                <CheckCircle size={16} color="#10b981" />
              }
              <strong style={{ color: migrateResult.error_count > 0 ? '#fca5a5' : '#6ee7b7' }}>
                {migrateResult.error_count > 0
                  ? `Completed with ${migrateResult.error_count} error(s)`
                  : `Successfully applied ${migrateResult.applied_count} migration(s)`
                }
              </strong>
            </div>
            {migrateResult.results && migrateResult.results
              .filter(r => r.status !== 'already_applied')
              .map((r, i) => (
                <div key={i} style={{ color: '#a3a3a3', fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", marginLeft: 24 }}>
                  v{r.version} {r.name}: {r.status === 'applied' ? '✅ applied' : `❌ ${r.error}`}
                </div>
              ))
            }
            {migrateResult.prerequisites?.satisfied && (
              <div style={{ color: '#6ee7b7', fontSize: 12, marginTop: 8, marginLeft: 24 }}>
                ✅ All prerequisites now satisfied — analytics loading…
              </div>
            )}
          </div>
        )}

        {/* Migration Error */}
        {migrateError && (
          <div style={gateStyles.errorBox}>
            <AlertTriangle size={14} color="#ef4444" />
            <span>{migrateError}</span>
          </div>
        )}

        {/* Confirmation + Button */}
        {!migrateResult?.prerequisites?.satisfied && (
          <div style={gateStyles.actionArea}>
            <label style={gateStyles.confirmLabel}>
              Type <code style={gateStyles.code}>MIGRATE</code> to confirm:
            </label>
            <input
              type="text"
              value={migrateConfirm}
              onChange={e => setMigrateConfirm(e.target.value)}
              placeholder="MIGRATE"
              style={{
                ...gateStyles.confirmInput,
                borderColor: confirmValid ? '#f59e0b66' : '#333',
              }}
              disabled={migrating}
              autoComplete="off"
              spellCheck="false"
            />
            <button
              onClick={onApply}
              disabled={!confirmValid || migrating || schemaLoading}
              style={{
                ...gateStyles.applyBtn,
                opacity: (!confirmValid || migrating) ? 0.5 : 1,
                cursor: (!confirmValid || migrating) ? 'not-allowed' : 'pointer',
              }}
            >
              {migrating ? (
                <>
                  <Loader size={14} className="animate-spin" />
                  Applying migrations…
                </>
              ) : (
                <>
                  <Play size={14} />
                  Run Migration
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const gateStyles = {
  container: { marginBottom: 24 },
  card: {
    background: '#141415', border: '1px solid #f59e0b33', borderRadius: 10,
    padding: '28px 32px',
  },
  iconRow: { marginBottom: 16 },
  title: {
    color: '#e5e5e5', fontSize: 18, fontWeight: 700, margin: '0 0 8px',
  },
  description: {
    color: '#737373', fontSize: 13, lineHeight: 1.6, margin: '0 0 20px',
    maxWidth: 640,
  },
  section: { marginBottom: 20 },
  sectionTitle: {
    color: '#a3a3a3', fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: 0.5, marginBottom: 8,
  },
  migrationItem: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
    background: '#0a0a0a', borderRadius: 6, border: '1px solid #1f1f1f',
    marginBottom: 4,
  },
  migrationVersion: {
    color: '#f59e0b', fontSize: 12, fontWeight: 700,
    fontFamily: "'IBM Plex Mono', monospace",
  },
  migrationName: {
    color: '#a3a3a3', fontSize: 12,
    fontFamily: "'IBM Plex Mono', monospace",
  },
  missingItem: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0',
    color: '#a3a3a3', fontSize: 12,
  },
  code: {
    background: '#1a1a1b', padding: '1px 5px', borderRadius: 3,
    fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#e5e5e5',
  },
  featureList: {
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  featureItem: {
    display: 'flex', alignItems: 'center', gap: 8, color: '#737373', fontSize: 12,
  },
  safetyNotice: {
    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
    background: '#3b82f60a', border: '1px solid #3b82f622', borderRadius: 6,
    color: '#93c5fd', fontSize: 12, lineHeight: 1.5, marginBottom: 20,
  },
  resultBox: {
    padding: '12px 16px', borderRadius: 6, border: '1px solid',
    marginBottom: 16, fontSize: 13,
  },
  errorBox: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
    background: '#ef444411', border: '1px solid #ef444433', borderRadius: 6,
    color: '#fca5a5', fontSize: 12, marginBottom: 16,
  },
  actionArea: { marginTop: 8 },
  confirmLabel: {
    display: 'block', color: '#737373', fontSize: 12, marginBottom: 8,
  },
  confirmInput: {
    display: 'block', width: 200, padding: '8px 12px', background: '#0a0a0a',
    border: '1px solid #333', borderRadius: 6, color: '#e5e5e5',
    fontSize: 14, fontFamily: "'IBM Plex Mono', monospace",
    marginBottom: 12, outline: 'none', boxSizing: 'border-box',
    letterSpacing: 1,
  },
  applyBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '10px 20px', background: '#f59e0b', border: 'none',
    borderRadius: 6, color: '#000', fontWeight: 700, fontSize: 13,
    cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace",
  },
};


// ── Sub-components ──────────────────────────────────────────────────────

function SummaryCard({ icon, label, value, sub, color }) {
  return (
    <div style={styles.summaryCard}>
      <div style={{ ...styles.cardIcon, color }}>{icon}</div>
      <div style={styles.cardLabel}>{label}</div>
      <div style={styles.cardValue}>{value}</div>
      <div style={styles.cardSub}>{sub}</div>
    </div>
  );
}

function InsightCard({ title, count, size, subtitle }) {
  return (
    <div style={styles.insightCard}>
      <div style={styles.insightTitle}>{title}</div>
      <div style={styles.insightValue}>
        {count} {count === 1 ? 'file' : 'files'} / {size}
      </div>
      <div style={styles.insightSub}>{subtitle}</div>
    </div>
  );
}

function HashCard({ icon, label, value, color }) {
  return (
    <div style={styles.hashCard}>
      <div style={{ color }}>{icon}</div>
      <div style={styles.hashLabel}>{label}</div>
      <div style={{ ...styles.hashValue, color }}>{value}</div>
    </div>
  );
}

// ── File Preview (read-only, reuses /api/admin/storage/preview endpoint) ─
function ObjectPreview({ s3Key, headers }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const isImage = IMAGE_EXTS.test(s3Key || '');
  const isVideo = VIDEO_EXTS.test(s3Key || '');

  useEffect(() => {
    if (!isImage || !s3Key) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/storage/preview?key=${encodeURIComponent(s3Key)}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { if (!cancelled) { setUrl(data.url); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [s3Key, isImage, headers]);

  const thumb = {
    width: 40, height: 40, borderRadius: 4, overflow: 'hidden',
    backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  };

  if (isImage && url && !error) {
    return (
      <div style={thumb}>
        <img
          src={url} alt="" loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setError(true)}
        />
      </div>
    );
  }

  if (isImage && loading) {
    return <div style={{ ...thumb, color: '#3a3a3a', fontSize: 8 }}>...</div>;
  }

  if (isVideo) {
    return (
      <div style={{ ...thumb, background: 'linear-gradient(135deg, #1a0a2a, #0a0a1a)', border: '1px solid #2a1a4a' }}>
        <Film size={16} color="#a78bfa" style={{ opacity: 0.7 }} />
      </div>
    );
  }

  // Non-previewable
  return (
    <div style={{ ...thumb, border: '1px solid #1f1f1f' }}>
      <FileQuestion size={14} color="#525252" />
    </div>
  );
}

function VideoPreviewModal({ s3Key, headers, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/admin/storage/preview?key=${encodeURIComponent(s3Key)}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => { if (!cancelled) { setUrl(data.url); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [s3Key, headers]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#141415', border: '1px solid #333', borderRadius: 10,
          padding: 16, maxWidth: 720, width: '90%', position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 8, right: 8, background: 'none',
            border: 'none', cursor: 'pointer', color: '#737373', padding: 4,
          }}
        >
          <X size={18} />
        </button>
        <div style={{ color: '#525252', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", marginBottom: 10, paddingRight: 24, wordBreak: 'break-all' }}>
          {s3Key}
        </div>
        {loading && <div style={{ color: '#525252', textAlign: 'center', padding: 40 }}>Loading preview…</div>}
        {error && <div style={{ color: '#ef4444', textAlign: 'center', padding: 40 }}>Preview unavailable: {error}</div>}
        {url && !error && (
          <video
            src={url}
            controls
            autoPlay={false}
            style={{ width: '100%', borderRadius: 6, background: '#000', maxHeight: 480 }}
          />
        )}
      </div>
    </div>
  );
}

function DonutChart({ data, colors, useIndex = false }) {
  if (!data || data.length === 0) {
    return <div style={styles.noData}>No data</div>;
  }

  const colorValues = Object.values(colors);

  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={2}
          label={({ name, label }) => `${name}: ${label}`}
          labelLine={false}
        >
          {data.map((entry, idx) => (
            <Cell
              key={entry.name}
              fill={useIndex ? colorValues[idx % colorValues.length] : (colors[entry.name.toLowerCase()] || '#666')}
            />
          ))}
        </Pie>
        <Tooltip
          formatter={(value) => _formatSize(value)}
          contentStyle={{ background: '#1a1a1b', border: '1px solid #333', borderRadius: 6, color: '#e5e5e5' }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

function StackedBarChart({ data }) {
  // Convert the env×type data into recharts format
  const allTypes = new Set();
  const envs = Object.keys(data);
  envs.forEach(env => {
    Object.keys(data[env]).forEach(t => allTypes.add(t));
  });

  const chartData = envs.map(env => {
    const row = { environment: env };
    for (const t of allTypes) {
      row[t] = data[env][t] ? data[env][t].size_bytes : 0;
    }
    return row;
  });

  const typeArray = [...allTypes];
  const typeColors = Object.values(TYPE_COLORS);

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <XAxis dataKey="environment" tick={{ fill: '#737373', fontSize: 11 }} />
        <YAxis tick={{ fill: '#737373', fontSize: 11 }} tickFormatter={_formatSize} />
        <Tooltip
          formatter={(value, name) => [_formatSize(value), _typeLabel(name)]}
          contentStyle={{ background: '#1a1a1b', border: '1px solid #333', borderRadius: 6, color: '#e5e5e5' }}
        />
        <Legend formatter={_typeLabel} />
        {typeArray.map((t, i) => (
          <Bar
            key={t}
            dataKey={t}
            stackId="a"
            fill={TYPE_COLORS[t] || typeColors[i % typeColors.length]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}


// ── Helpers ──────────────────────────────────────────────────────────────

function _timeAgo(isoStr) {
  if (!isoStr) return 'never';
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  return `${Math.round(diff / 3600)}h ago`;
}

function _formatSize(bytes) {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 ** 3)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function _typeLabel(type) {
  const labels = {
    original_videos: 'Original Videos',
    proxy_videos: 'Video Proxies',
    image_uploads: 'Image Uploads',
    image_exports: 'Image Exports',
    video_exports: 'Video Exports',
    backgrounds: 'Backgrounds',
    presets: 'Presets',
    backups: 'Backups',
    system_files: 'System Files',
    orphan_candidates: 'Orphan Candidates',
    unknown: 'Unknown',
  };
  return labels[type] || type;
}

function _truncateKey(key) {
  if (!key) return '';
  if (key.length <= 60) return key;
  return '…' + key.slice(-57);
}


// ── Analytics Usage Modal (lightweight version) ───────────────────────────

function AnalyticsUsageModal({ storageKey, headers, onClose }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const params = new URLSearchParams();
    if (storageKey) params.set('storage_key', storageKey);

    fetch(`${BACKEND_URL}/api/admin/storage/asset-usage?${params.toString()}`, { headers })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [storageKey, headers]);

  const us = data?.usage_summary || {};

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div style={{
        background: '#141414', border: '1px solid #2a2a2a', borderRadius: 10,
        width: 700, maxHeight: '85vh', overflow: 'auto',
        padding: 0, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid #1f1f1f',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Search size={16} color="#818cf8" />
            <span style={{ color: '#e5e5e5', fontWeight: 700, fontSize: 14 }}>Asset Usage Inspector</span>
            <span style={{
              padding: '2px 8px', background: '#0c1929', borderRadius: 4,
              border: '1px solid #1e40af', color: '#60a5fa', fontSize: 9, fontWeight: 700,
            }}>DIAGNOSTIC ONLY</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#525252' }}>
            <X size={18} />
          </button>
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: '#525252' }}>Loading usage report...</div>}
        {error && <div style={{ padding: 20, color: '#f87171' }}>Error: {error}</div>}

        {data && (
          <div style={{ padding: '16px 20px' }}>
            {/* Asset metadata */}
            {data.asset ? (
              <div style={{ marginBottom: 16, padding: 12, background: '#0a0a0a', borderRadius: 6, border: '1px solid #1f1f1f' }}>
                <div style={{ fontSize: 10, color: '#525252', marginBottom: 6, fontWeight: 600 }}>ASSET METADATA</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 11 }}>
                  <div><span style={{ color: '#525252' }}>ID: </span><span style={{ color: '#a3a3a3', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10 }}>{data.asset.asset_id}</span></div>
                  <div><span style={{ color: '#525252' }}>Kind: </span><span style={{ color: '#a3a3a3' }}>{data.asset.kind}</span></div>
                  <div><span style={{ color: '#525252' }}>Status: </span><span style={{ color: data.asset.status === 'confirmed' ? '#4ade80' : '#f87171' }}>{data.asset.status}</span></div>
                  <div><span style={{ color: '#525252' }}>Size: </span><span style={{ color: '#a3a3a3' }}>{data.asset.size_bytes ? `${(data.asset.size_bytes / 1024 / 1024).toFixed(2)} MB` : '—'}</span></div>
                  {data.asset.orphaned_at && <div><span style={{ color: '#f87171' }}>Orphaned: {new Date(data.asset.orphaned_at).toLocaleString()}</span></div>}
                </div>
                <div style={{ marginTop: 6, fontSize: 10, color: '#3a3a3a', fontFamily: "'IBM Plex Mono', monospace", wordBreak: 'break-all' }}>
                  {data.asset.storage_key}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 16, padding: 12, background: '#0a0a0a', borderRadius: 6, border: '1px solid #1f1f1f', color: '#737373', fontSize: 12 }}>
                No media_assets record found for this S3 key.
                {data.lookup_confidence === 'low' && ' (limited confidence — lookup by storage_key only)'}
              </div>
            )}

            {/* Usage Summary */}
            <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {[
                ['In Carousel JSONB', us.used_in_current_carousel_jsonb],
                ['In asset_references', us.used_in_asset_references_table],
                ['In render_jobs', us.used_in_render_jobs],
                ['In backups/system', us.used_in_backups || us.used_in_system_objects],
              ].map(([label, val]) => (
                <div key={label} style={{
                  padding: '8px 10px', borderRadius: 6, textAlign: 'center',
                  background: val ? '#052e1622' : '#0a0a0a',
                  border: `1px solid ${val ? '#16653444' : '#1f1f1f'}`,
                }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: val ? '#4ade80' : '#3a3a3a' }}>{val ? 'Yes' : 'No'}</div>
                  <div style={{ fontSize: 9, color: '#525252', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Verdict */}
            <div style={{
              padding: '10px 14px', marginBottom: 16, borderRadius: 6,
              background: us.is_really_used_by_current_slides ? '#052e16' : '#1c0a0a',
              border: `1px solid ${us.is_really_used_by_current_slides ? '#166534' : '#991b1b'}`,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: us.is_really_used_by_current_slides ? '#4ade80' : '#f87171' }}>
                {us.is_really_used_by_current_slides ? '✓ Used in current carousel slides' : '✗ Not found in current carousel JSONB'}
              </div>
              {us.suspected_stale_reference && (
                <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 4 }}>⚠ Suspected stale reference</div>
              )}
              <div style={{ fontSize: 10, color: '#525252', marginTop: 4 }}>
                {data.carousel_count_scanned} carousel(s) scanned · Fields: {(data.carousel_asset_fields_checked || []).join(', ')}
              </div>
            </div>

            {/* Where used */}
            {data.carousel_jsonb_references.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: '#525252', marginBottom: 6, fontWeight: 600 }}>WHERE USED</div>
                {data.carousel_jsonb_references.map((ref, i) => (
                  <div key={i} style={{ padding: '4px 8px', fontSize: 11, color: '#a3a3a3', borderLeft: '2px solid #166534', marginBottom: 4, background: '#052e1611' }}>
                    <strong>{ref.carousel_title}</strong> → {ref.field_path} (slide #{ref.slide_index ?? '—'})
                  </div>
                ))}
              </div>
            )}

            {/* Mismatches */}
            {data.mismatches.length > 0 && (
              <div>
                <div style={{ fontSize: 10, color: '#525252', marginBottom: 6, fontWeight: 600 }}>MISMATCHES</div>
                {data.mismatches.map((m, i) => (
                  <div key={i} style={{
                    padding: '6px 10px', marginBottom: 4, borderRadius: 4, fontSize: 11,
                    background: m.severity === 'warning' ? '#1c1917' : '#0a0a0a',
                    border: `1px solid ${m.severity === 'warning' ? '#854d0e44' : '#1f1f1f'}`,
                    color: '#a3a3a3',
                  }}>
                    <span style={{ color: m.severity === 'warning' ? '#fbbf24' : '#60a5fa', fontWeight: 600, marginRight: 6, fontSize: 9 }}>
                      {m.type.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    {m.message}
                  </div>
                ))}
              </div>
            )}

            {/* Tracking sources */}
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10, color: '#525252', marginBottom: 6, fontWeight: 600 }}>TRACKING SOURCES</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(data.tracking_sources).map(([source, present]) => (
                  <span key={source} style={{
                    padding: '3px 10px', borderRadius: 4, fontSize: 10,
                    background: present ? '#052e1622' : '#0a0a0a',
                    border: `1px solid ${present ? '#16653444' : '#1f1f1f'}`,
                    color: present ? '#4ade80' : '#3a3a3a',
                  }}>
                    {present ? '●' : '○'} {source}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Styles ──────────────────────────────────────────────────────────────

const styles = {
  loginContainer: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', background: '#0a0a0a',
  },
  loginCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 12,
    padding: 32, textAlign: 'center', width: 340,
  },
  loginTitle: { color: '#e5e5e5', fontSize: 18, margin: '12px 0 20px' },
  loginInput: {
    width: '100%', padding: '10px 12px', background: '#0a0a0a',
    border: '1px solid #333', borderRadius: 6, color: '#e5e5e5',
    fontSize: 14, marginBottom: 12, outline: 'none', boxSizing: 'border-box',
  },
  loginButton: {
    width: '100%', padding: '10px 0', background: '#f59e0b',
    border: 'none', borderRadius: 6, color: '#000', fontWeight: 600,
    fontSize: 14, cursor: 'pointer',
  },

  page: { padding: '24px 32px', maxWidth: 1400 },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 24,
  },
  title: { color: '#e5e5e5', fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle: { color: '#525252', fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" },
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', background: '#1a1a1b', border: '1px solid #333',
    borderRadius: 6, color: '#a3a3a3', fontSize: 12, cursor: 'pointer',
    fontFamily: "'IBM Plex Mono', monospace",
  },

  errorBanner: {
    background: '#ef4444' + '22', border: '1px solid #ef444466', borderRadius: 6,
    padding: '10px 14px', color: '#fca5a5', fontSize: 13, marginBottom: 16,
  },

  // Summary Cards
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 12, marginBottom: 24,
  },
  summaryCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 8,
    padding: '14px 16px',
  },
  cardIcon: { marginBottom: 8 },
  cardLabel: { color: '#737373', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
  cardValue: { color: '#e5e5e5', fontSize: 20, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" },
  cardSub: { color: '#525252', fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" },

  // Charts
  chartsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16, marginBottom: 24,
  },
  chartCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 8,
    padding: 16,
  },
  chartTitle: {
    color: '#a3a3a3', fontSize: 13, fontWeight: 600, marginBottom: 12,
    display: 'flex', alignItems: 'center',
  },
  fullWidthCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 8,
    padding: 16, marginBottom: 24,
  },
  noData: { color: '#525252', textAlign: 'center', padding: 40, fontSize: 13 },

  // Insight Cards
  insightGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12, marginBottom: 24,
  },
  insightCard: {
    background: '#141415', border: '1px solid #262626', borderRadius: 8,
    padding: '12px 14px',
  },
  insightTitle: { color: '#737373', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  insightValue: { color: '#e5e5e5', fontSize: 14, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" },
  insightSub: { color: '#525252', fontSize: 11, marginTop: 2 },

  // Hash Status
  hashGrid: {
    display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
  },
  hashCard: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
    background: '#0a0a0a', borderRadius: 6, border: '1px solid #1f1f1f',
  },
  hashLabel: { color: '#737373', fontSize: 11 },
  hashValue: { fontSize: 16, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" },
  hashDivider: {
    width: 1, height: 32, background: '#262626',
  },

  // Tables
  tableContainer: { overflowX: 'auto' },
  table: {
    width: '100%', borderCollapse: 'collapse', fontSize: 12,
    fontFamily: "'IBM Plex Mono', monospace",
  },
  th: {
    textAlign: 'left', padding: '8px 12px', color: '#737373',
    borderBottom: '1px solid #262626', fontSize: 11, textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  td: { padding: '8px 12px', color: '#a3a3a3', borderBottom: '1px solid #1a1a1b' },
  tdKey: {
    padding: '8px 12px', color: '#a3a3a3', borderBottom: '1px solid #1a1a1b',
    maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    fontSize: 11,
  },
  trEven: { background: '#0f0f10' },
  envBadge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
  },

  // Preview
  previewBtn: {
    background: 'none', border: '1px solid #262626', borderRadius: 4,
    cursor: 'pointer', color: '#525252', padding: '3px 4px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'color 0.15s, border-color 0.15s',
  },
};
